"use client";

import type { UrlObject } from "url";
import type { ComponentProps } from "react";
import { forwardRef, useCallback, useMemo } from "react";

import NextLink from "next/link";
import { usePathname, useRouter } from "next/navigation";

import type { LanguageCode } from "@repo/locale";
import { getActiveLocaleFromPath, stripLocaleFromPath, withLocalePrefix } from "@repo/locale";

import { CURRENT_ZONE, isUnderBase } from "../constants/bookingBase";

type Href = string | UrlObject;

const isInternalAbsolute = (p: string) => p.startsWith("/") && !p.startsWith("//");

/**
 * Re-attach this app's zone segment to an in-app path.
 *
 * The zone lives in the route tree (`app/[country]/[lang]/<zone>/...`), not in
 * basePath, so an in-app path has to carry it. Upstream writes bare in-zone paths
 * (`router.push("/flight")`) because *their* zone comes from basePath, and merged
 * code keeps arriving in that shape — this is what makes those calls land on
 * `/cl/es/booking/flight` instead of 404ing at `/cl/es/flight`.
 *
 * Idempotent: a path already under the zone is returned unchanged, and matching is
 * segment-safe, so `/bookingV2/x` is never mistaken for `/booking/x`. The main app
 * has no zone (`CURRENT_ZONE` undefined) and is unaffected, as is agencies while it
 * stays unmigrated.
 */
function withZonePrefix(path: string): string {
  if (!CURRENT_ZONE) return path;
  const zoneSeg = `/${CURRENT_ZONE}`;
  return isUnderBase(path, zoneSeg) ? path : `${zoneSeg}${path}`;
}

/**
 * Turn an in-app href into `/{country}/{lang}/{zone}/rest`.
 *
 * Never includes APP_BASE (`/fly`) — Next prepends basePath to Link/router hrefs
 * itself, so adding it here would produce `/fly/fly/...`. For browser-level or
 * cross-app navigation use `zoneAbsoluteUrl`/`homeUrl` from `constants/bookingBase`,
 * which do include it.
 *
 * External / protocol-relative (`//host`) / hash-only / query-only / relative links
 * are left alone — that guard is what keeps CMS-supplied hrefs safe (CMS‑1), since
 * the CMS localizes its own URLs and emits absolute or protocol-less values.
 */
export function localizeHref(href: Href, code: LanguageCode | null): Href {
  const rewrite = (path: string): string => {
    if (!isInternalAbsolute(path)) return path;
    // work on the un-prefixed remainder so both bare and already-localized
    // hrefs converge on one result (idempotent for repeated application)
    const active = getActiveLocaleFromPath(path);
    const bare = active ? stripLocaleFromPath(path) : path;
    const zoned = withZonePrefix(bare);
    const effective = active ?? code;
    return effective ? withLocalePrefix(zoned, effective) : zoned;
  };
  if (typeof href === "string") return rewrite(href);
  if (href && typeof href.pathname === "string")
    return { ...href, pathname: rewrite(href.pathname) };
  return href;
}

/** Active URL locale code (`es_cl`) from the current path, or null if none. */
export function useActiveLocale(): LanguageCode | null {
  return getActiveLocaleFromPath(usePathname() ?? "");
}

/** Current pathname with the leading /{country}/{lang} removed. */
export function useLocalizedPathname(): string {
  return stripLocaleFromPath(usePathname() ?? "/");
}

/**
 * Current pathname reduced to its in-zone remainder: `/cl/es/staff/login` → `/login`.
 *
 * This is the one to compare against route literals in shared components. Stripping
 * only the locale leaves `/staff/login`, which matches no bare literal — the PATH‑1
 * bug class, one segment deeper. Because the result is zone-relative it also matches
 * upstream's literals verbatim (their zone lives in basePath, so their pathname is
 * zone-relative too), which keeps these comparisons out of future merge conflicts.
 * `usePathname()` already excludes basePath, so APP_BASE never appears here.
 */
export function useZonePathname(): string {
  const withoutLocale = stripLocaleFromPath(usePathname() ?? "/");
  if (!CURRENT_ZONE) return withoutLocale;
  const zoneSeg = `/${CURRENT_ZONE}`;
  return isUnderBase(withoutLocale, zoneSeg)
    ? withoutLocale.slice(zoneSeg.length) || "/"
    : withoutLocale;
}

/** useRouter with locale-preserving push/replace/prefetch. */
export function useLocalizedRouter() {
  const router = useRouter();
  const code = useActiveLocale();
  // Forward extra args via rest params so the wrappers are arity-transparent
  // (calling `.push("/x")` must not become `.push("/x", undefined)`).
  const push = useCallback(
    (h: Href, ...rest: [Parameters<typeof router.push>[1]?]) =>
      router.push(localizeHref(h, code) as string, ...rest),
    [router, code],
  );
  const replace = useCallback(
    (h: Href, ...rest: [Parameters<typeof router.replace>[1]?]) =>
      router.replace(localizeHref(h, code) as string, ...rest),
    [router, code],
  );
  const prefetch = useCallback(
    (h: string, ...rest: [Parameters<typeof router.prefetch>[1]?]) =>
      router.prefetch(localizeHref(h, code) as string, ...rest),
    [router, code],
  );
  return useMemo(() => ({ ...router, push, replace, prefetch }), [router, push, replace, prefetch]);
}

/** Drop-in replacement for next/link that keeps the active locale prefix. */
export const Link = forwardRef<HTMLAnchorElement, ComponentProps<typeof NextLink>>(function Link(
  { href, ...props },
  ref,
) {
  const code = useActiveLocale();
  return <NextLink ref={ref} href={localizeHref(href, code)} {...props} />;
});
